import { pdsTv } from '@nestle-npus-nbm/pds-ui';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import { sendIt } from 'gatsby-plugin-purina-analytics/common/functions';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { themeVariant } from 'src/theme/basic-styles';
import { CMS_THEME } from '../../common/enums';
import { RatingStars } from '../RatingStars/RatingStars';
import { CmsButtonLink } from '../button/CmsButton';
import PricespiderWidget from '../pricespider/PricespiderWidget';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  cmsTheme: CMS_THEME;
  title: string;
  review: string;
  author: string;
  rating: number;
  alignment?: 'left' | 'right';
  isVertical: boolean;
  imgComponent?: React.ReactNode;
  imgCover: boolean;
  sku?: string;
  reviewLink?: string;
  product: IStorageReview['relationships']['product'][0];
}

const imageContainer = pdsTv({
  base: 'pds-flex pds-h-auto pds-w-[100%] pds-justify-center pds-overflow-hidden pds-rounded-xl md:pds-h-[318px]',
  variants: {
    theme: themeVariant(),
    direction: {
      vertical: 'pds-w-full pds-min-w-full pds-rounded-bl-none pds-rounded-br-none',
      horizontal: 'pds-min-w-[473px] pds-rounded-bl-xl pds-rounded-br-xl lg:pds-flex-[0_0_50%]',
    },
    addPadding: {
      true: 'pds-p-4',
    },
  },
});

const card = pdsTv({
  base: 'vstack pds-mb-0 pds-rounded-b-xl pds-p-5',
  variants: {
    theme: themeVariant(),
    direction: {
      horizontal: 'pds-flex-grow-0 pds-bg-transparent lg:!pds-p-0',
      vertical: 'pds-flex-grow pds-p-4 sm:pds-p-5',
    },
    textCenter: {
      true: 'pds-text-center',
    },
  },
  compoundVariants: [
    {
      direction: 'vertical',
      theme: 'light',
      class: 'pds-bg-paper',
    },
    {
      direction: 'vertical',
      theme: 'dark',
      class: 'pds-bg-[#464646]',
    },
  ],
});

const wrapper = pdsTv({
  base: 'pds-flex pds-h-full',
  variants: {
    theme: themeVariant({ isOnlyText: true }),
    direction: {
      vertical: 'pds-flex-col',
      horizontal: 'pds-flex-row pds-items-center pds-gap-[3rem]',
    },
    align: {
      left: 'lg:pds-flex-row-reverse',
      right: 'lg:pds-flex-row',
      center: '',
    },
  },
});

const buttonWrapper = pdsTv({
  base: 'pds-mt-auto pds-flex pds-flex-col pds-gap-4',
  variants: {
    buttonWidth: {
      full: '[&>*]:pds-flex-grow [&>div>a]:pds-h-[56px]',
      auto: '',
    },
    alignment: {
      left: '',
      right: '',
      center: '',
    },
  },
  compoundVariants: [
    {
      buttonWidth: 'full',
      alignment: ['left', 'right'],
      class: 'lg:pds-flex-row [&>div]:lg:pds-flex-[0_0_50%]',
    },
  ],
});

const ProductReview = ({
  cmsTheme = CMS_THEME.LIGHT,
  title,
  review,
  author,
  rating,
  alignment = 'left',
  isVertical = false,
  imgComponent,
  imgCover = false,
  sku,
  reviewLink,
  product,
  ...props
}: Props) => {
  const breakpoint = useBreakpoint();
  const isMobile = !breakpoint.lg;
  const starRating = Math.min(Math.max(rating, 0), 5);
  const direction = isVertical || isMobile ? 'vertical' : 'horizontal';
  const contentAlignment = !imgComponent || isVertical ? 'center' : alignment;
  const { t } = useTranslation();

  const wrapperStyles = React.useMemo(
    () =>
      wrapper({
        theme: cmsTheme,
        direction,
        className: props.className,
        align: contentAlignment,
      }),
    [cmsTheme, direction, props.className, contentAlignment],
  );

  const imageContainerStyles = React.useMemo(
    () =>
      imageContainer({
        theme: cmsTheme,
        direction,
        addPadding: imgCover,
      }),
    [cmsTheme, direction, imgCover],
  );

  const cardStyles = React.useMemo(
    () =>
      card({
        theme: cmsTheme,
        direction,
        textCenter: Boolean(!imgComponent),
      }),
    [cmsTheme, direction, imgComponent],
  );

  const buttonsWrapperStyles = React.useMemo(
    () =>
      buttonWrapper({
        buttonWidth: (sku && reviewLink) || direction === 'vertical' ? 'full' : 'auto',
        alignment: !isVertical ? alignment : undefined,
      }),
    [sku, reviewLink, direction, isVertical, alignment],
  );

  return (
    <div {...props} className={wrapperStyles}>
      {imgComponent && <div className={imageContainerStyles}>{imgComponent}</div>}
      <figure className={cardStyles}>
        <div className="pds-flex pds-items-center">
          <RatingStars rating={rating} theme={cmsTheme} />
          &nbsp;
          <span aria-hidden className="pds-text-nowrap pds-text-body-md pds-font-bold">
            {t('{{starRating}} out of 5 stars', { starRating })}
          </span>
        </div>
        <span className="pds-mb-3 pds-mt-4 pds-text-title-sm pds-font-extrabold">
          {title}
          <span className="sr-only">{t(': {{starRating}} out of 5', { starRating })}</span>
        </span>
        <q
          className="pds-text-body-md"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: review }}
        />
        <figcaption className={`pds-text-body-md ${sku || reviewLink ? 'pds-mb-5' : null}`}>
          &mdash;&nbsp;{author}
        </figcaption>
        {(sku || reviewLink) && (
          <div className={buttonsWrapperStyles}>
            {sku && (
              <div>
                <div id={sku} hidden>
                  {product.title}
                </div>
                <PricespiderWidget
                  // TODO: to consider having this be determined by “Primary Button” behavior in CMS
                  outlined={cmsTheme === CMS_THEME.DARK}
                  title={title}
                  id={sku}
                  cmsTheme={cmsTheme}
                />
              </div>
            )}
            {Boolean(reviewLink) && (
              <CmsButtonLink
                analytics={false}
                className="pds-p-4 pds-capitalize"
                variant={cmsTheme === CMS_THEME.LIGHT ? 'btn-outline' : 'tonal'}
                cmsTheme={cmsTheme}
                to={`${reviewLink}#reviews`}
                onClick={() =>
                  sendIt({
                    event: 'module_interaction',
                    eventParams: {
                      component_id: 'primary_cta: read more reviews',
                      component_type: 'reviews_cta',
                      content_name: 'review_card',
                      review_link: reviewLink,
                    },
                  })
                }
              >
                {t('Read more reviews')}
                <span className="pds-sr-only">
                  {t('About')} {product.title}
                </span>
              </CmsButtonLink>
            )}
          </div>
        )}
      </figure>
    </div>
  );
};

export default ProductReview;
